"use client";

import { useState, useEffect, type ReactNode } from "react";
import Image from "next/image";
import { PawIcon } from "@/components/ui/PawIcon";

const QUIZ_LOADING_MESSAGES = [
  "Tracking your perfect safari...",
  "Negotiating with the elephants...",
  "Following fresh footprints...",
  "Calculating river crossing...",
] as const;

/**
 * Fixed message used for the results-loading phase so both the route-level
 * loading.tsx and the Suspense fallback in page.tsx show the same stable text,
 * preventing a visible message flip mid-load.
 */
export const RESULTS_LOADING_MESSAGE = "Tracking your perfect safari...";

// Moved to lib/turnstile/messages.ts so AuthModal can use it without importing from a
// quiz component; re-exported so existing importers keep working.
export { TURNSTILE_INTERACTIVE_MESSAGE } from "@/lib/turnstile/messages";

/** Minimum time the splash stays up, so its message never flickers by on a fast connection. */
export const MIN_SPLASH_MS = 3000;

/**
 * Branded full-screen loading splash shown during quiz initialisation and while
 * matching lodges are fetched.
 *
 * When `message` is provided it is rendered directly — deterministic, no flip,
 * hydration-safe. Pass it for the results phase so the route-level loading.tsx
 * and the Suspense fallback always show the same text.
 *
 * When omitted, a random safari message is chosen after the first paint
 * (hydration-safe: index 0 on server + first client render) and held for the
 * entire duration — no rotation.
 */
export function QuizLoadingSplash({
  message,
  extraContent,
}: {
  message?: string;
  /** Optional content rendered below the loading phrase — used by QuizWizard to
   *  mount the (normally invisible) Turnstile widget so an escalated challenge
   *  appears in a visible location. */
  extraContent?: ReactNode;
}) {
  const [randomMessage, setRandomMessage] = useState<string>(QUIZ_LOADING_MESSAGES[0]);
  // Empty on server + first client render (hydration-safe); set after mount so
  // the live region sees a content *change* — which is what screen readers
  // announce. Text already present when role="status" renders (e.g. SSR
  // results loading.tsx) is typically not announced.
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (message !== undefined) return;
    // Pick once after mount and keep it — no interval, no rotation.
    // setTimeout defers past hydration so the server and first client render
    // both show index 0 (avoids a hydration mismatch).
    const t = setTimeout(() => {
      setRandomMessage(
        QUIZ_LOADING_MESSAGES[Math.floor(Math.random() * QUIZ_LOADING_MESSAGES.length)]!,
      );
    }, 0);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    // setTimeout defers past React's initial render batch, so screen readers
    // observe a genuine content change in the live region on both the SSR
    // results path and the client-mounted wizard path. Announces the explicit
    // `message` when one is set (the visible phrase is aria-hidden, and the
    // Turnstile "confirm you're human" prompt must reach screen readers too),
    // and re-announces when it changes mid-splash.
    const t = setTimeout(() => {
      setAnnouncement(message ?? "Loading your safari recommendations");
    }, 0);
    return () => clearTimeout(t);
  }, [message]);

  const displayMessage = message ?? randomMessage;

  return (
    <div
      className="bsa-loading-splash"
      role="status"
      aria-live="polite"
    >
      {/* sr-only text is actual live-region content — aria-label on the container
          isn't announced by most screen readers on attribute change. Empty on
          first render so the post-mount state update registers as a content
          change, which is what triggers screen-reader announcement. */}
      <span className="sr-only">{announcement}</span>
      <div className="bsa-loading-stack">
        <Image
          src="/logo.svg"
          alt="BestSafari.Africa"
          width={606}
          height={311}
          priority
          className="bsa-loading-logo"
        />
        <div className="bsa-loading-inner">
          <div className="bsa-loading-phrase-wrap">
            {/* key={displayMessage} forces a remount when the random phrase is
                picked (setTimeout 0), so bsaPhraseIn animates the chosen phrase
                rather than only index 0. For the fixed results message the key
                is stable and the animation plays exactly once. */}
            <span className="bsa-loading-phrase" aria-hidden="true" key={displayMessage}>
              {displayMessage}
            </span>
          </div>
          <PawTrail />
          {/* Turnstile / security-check widget slot — normally invisible;
              renders an inline challenge when Cloudflare escalates. */}
          {extraContent}
        </div>
      </div>
    </div>
  );
}

const PAW_COUNT = 7;
// Must match bsaPawWave duration in globals.css (.bsa-paw) — change both together.
const PAW_CYCLE_S = 3.75;

function PawTrail() {
  return (
    <div className="bsa-paw-trail" aria-hidden="true">
      {Array.from({ length: PAW_COUNT }).map((_, i) => (
        <span
          key={i}
          className="bsa-paw"
          // Negative delay makes a paw *lead* by that amount, so the paw with
          // the least lead (highest index) peaks last. Staggering by
          // (PAW_COUNT - i) makes the peak travel index-ascending, i.e. the
          // highlight sweeps left-to-right across the row. The paws face up
          // (native `PawIcon` orientation) rather than tracking this
          // direction — that's a deliberate choice, not a bug.
          style={{ animationDelay: `${-((PAW_COUNT - i) / PAW_COUNT) * PAW_CYCLE_S}s` }}
        >
          <PawIcon flip={i % 2 === 1} />
        </span>
      ))}
    </div>
  );
}
